import { LoaderFunctionArgs, Outlet, RouteObject } from 'react-router-dom';
import ProjectDetails from '@app/modules/projects/inputs/project-admin/ProjectDetails';
import ProjectRoutesWrapper from '@app/modules/projects/project-routes-wrapper/ProjectRoutesWrapper';
import { ContentHeader } from '@app/shared/layout/content-header/ContentHeader';
import { CapitalStructureNavigation } from '@app/modules/projects/inputs/capital-structure/CapitalStructureNavigation';
import { SourcesAndUsesContent } from '@app/modules/projects/inputs/capital-structure/tabs/sources-and-uses-tab/SourcesAndUsesContent';
import { EquityBridgeContent } from '@app/modules/projects/inputs/capital-structure/tabs/equity-bridge-tab/EquityBridgeContent';
import PwcNavigate from '@app/shared/routing/PwcNavigate';
import { ValuationInputsNavigation } from '@app/modules/projects/inputs/valuation/ValuationInputsNavigation';
import { CapmAssumptions } from '@app/modules/projects/inputs/valuation/capm-assumptions/CapmAssumptions';
import { useAppSelector } from '@core/hooks/redux-hooks';
import Projects from '@app/modules/projects/projects/Projects';
import { EmptyValues } from '@app/shared/constants/empty-values';
import DashboardOpm from '@app/modules/projects/dashboard/DashboardOpm';
import RealWorldDistributionsOpm from './outputs/real-world-distributions/RealWorldDistributions';
import { DashboardHeader } from '@app/modules/projects/dashboard/DashboardHeader';
import { ContentHeaderLayout } from '@app/modules/projects/project-routes-wrapper/layout-enums';
import { HeaderVariations } from '@app/shared/layout/content-header/types';
import BenchmarkingInputs2 from '@app/modules/projects/inputs/benchmarking-inputs-2/BenchmarkingInputs2';
import { DeleteProjectAction } from '@app/modules/projects/inputs/project-admin/DeleteProjectAction';
import { CopyProjectAction } from '@app/modules/projects/inputs/project-admin/copy-project/CopyProjectAction';
import store from '@core/store/store';
import { fetchAndRecalculate, projectSlice } from '@core/store/project-slice';
import { LoadingStatus } from '@app/shared/models/loading-status-enum';
import { setHighlightEditableCells } from '@app/core/store/ui-values-slice';
import { ProjectAccessAction } from '@app/modules/projects/inputs/project-admin/project-access/ProjectAccessAction';
import { GlobalHistory } from '@app/shared/components/global-navigate/GlobalNavigate';
import { RouteConstants } from './RouteConstants';
import { OpmWaterfallNavigation } from './outputs/opm-summary-waterfall/OpmWaterfallNavigation';
import SampleWaterfallTable from './outputs/opm-summary-waterfall/sample-waterfall/SampleWaterfallTable';
import { AddSampleWaterfall } from './outputs/opm-summary-waterfall/sample-waterfall/AddSampleWaterfall';
import { Pwerm2WaterfallTable } from './outputs/pwerm2-summary-waterfall/Pwerm2WaterfallTable';
import Pwerm2Dashboard from './dashboard/Pwerm2Dashboard';
import { CapitalStructurePage } from './inputs/capital-structure/CapitalStructurePage';
import { ValuationInputsPage } from './inputs/valuation/ValuationInputsPage';
import { OpmWaterfallPage } from './outputs/opm-summary-waterfall/OpmWaterfallPage';
import { CostOfEquity2Navigation } from './outputs/cost-of-equity-2/CostOfEquity2Navigation';
import { CostOfEquity2Page } from './outputs/cost-of-equity-2/CostOfEquity2Page';
import SummaryAndHighLevelData2 from './outputs/cost-of-equity-2/summary-and-high-level-data/SummaryAndHighLevelData2';
import TotalEquityCrossCheck2 from './outputs/cost-of-equity-2/total-equity-cross-check/TotalEquityCrossCheck2';
import { InstrumentLevelData2 } from './outputs/cost-of-equity-2/instrument-level-data/InstrumentLevelData2';
import { DealThesisNavigation } from './inputs/deal-thesis-2/DealThesisNavigation';
import CapitalStructureSidebarErf from '@app/shared/layout/capital-structure-sidebar/CapitalStructureSidebarErf';
import BuildStructureContentErf from './inputs/capital-structure/tabs/build-structure-erf/BuildStructureContentErf';
import { BuildStructureProvider } from './inputs/capital-structure/tabs/build-structure-erf/BuildStructureContext';
import { AddEvent } from './inputs/capital-structure/tabs/build-structure-erf/add-event/AddEvent';
import { PayoutLogicMainErf } from './inputs/capital-structure/tabs/payout-logic-erf/PayoutLogicMainErf';
import { PayoutLogicErf } from './inputs/capital-structure/tabs/payout-logic-erf/PayoutLogicErf';
import { ParticipationTableErf } from './inputs/capital-structure/tabs/participation-table-erf/ParticipationTableErf';
import CapTableErf from './inputs/capital-structure/tabs/cap-table-erf/CapTableErf';
import { CapitalStructureTabContent } from './inputs/capital-structure/tabs/CapitalStructureTabContent';
import PwermOpmConclusions from './outputs/overall-conclusion/PwermOpmConclusions';
import { clearPwerm2Results } from '@app/core/store/pwerm2-calculation-slice';
import FirstLastTableErf from './outputs/opm-summary-waterfall/first-last-5-erf/FirstLastTableErf';
import { SponsorIRRAndHurdlePage } from './outputs/other-calculations/sponsor-irr-and-hurdle/SponsorIRRAndHurdlePage';
import { SponsorIRRAndHurdleNavigation } from './outputs/other-calculations/sponsor-irr-and-hurdle/SponsorIRRAndHurdleNavigation';
import IRRHurdleCalculations from './outputs/other-calculations/sponsor-irr-and-hurdle/irr-hurdle-calculations/IRRHurdleCalculations';
import SponsorIRRTable from './outputs/other-calculations/sponsor-irr-and-hurdle/sponsor-irr/SponsorIRRTable';
import { OutletByCaseId } from '@app/shared/routing/OutletByCaseId';
import { CaseNavigationControls } from './inputs/capital-structure/controls/CaseNavigationControls';
import SponsorIRRAndHurdleFocusMode from './outputs/other-calculations/sponsor-irr-and-hurdle/SponsorIRRAndHurdleFocusMode';
import { SponsorIrrAndHurdleTab } from '@app/shared/models/contracts/enums/shared-enums';
import { FocusModeOutlet } from '@app/shared/hooks/focus-mode';
import { DividendsTable } from './inputs/capital-structure/tabs/dividends-table/DividendsTable';
import { DividendsPage } from './inputs/capital-structure/tabs/dividends-table/DividendsPage';
import DLOM from './inputs/valuation/minority-discounts-2/DLOM';
import DLOC from './inputs/valuation/minority-discounts-2/DLOC';
import DiscountRateOverride from './inputs/valuation/discount-rate-override/DiscountRateOverride';
import { i18nSetup, l } from '@locale/setupI18n';
import CurrencyLabel from '@app/shared/components/currency-label/CurrencyLabel';
import HighlightEditableCells from '@app/shared/components/highlight-editable-cells/HighlightEditableCells';
import ShowPerShareFigures from '@app/shared/components/show-per-share-figures/ShowPerShareFigures';
import ExpandAllSponsorIRRAndHurdle, {
  SponsorIRRAndHurdleTabs,
} from '@app/shared/components/expand-all/ExpandAllSponsorIRRAndHurdle';
import BenchmarkingOnly from '@app/shared/components/benchmarking-only/BenchmarkingOnly';
import ExpandAllPwermWaterfallTable from '@app/shared/components/expand-all/ExpandAllPwerm';
import ExpandAllFirstLastWaterfall from '@app/shared/components/expand-all/ExpandAllFirstLastWaterfall';
import OpmInputsPage from './inputs/opm-inputs-2/OpmInputsPage';
import { DealThesisPage } from './inputs/deal-thesis-2/DealThesisPage';
import EnableDiscountRateOverride from '@app/shared/components/discount-rate-override/EnableDiscountRateOverride';
import ExpandAllDiscountRateOverride from '@app/shared/components/expand-all/ExpandAllDiscountRateOverride';
import EnableShowOriginalDiscountRates from '@app/shared/components/discount-rate-override/EnableShowOriginalDiscountRates';

i18nSetup();

const ProjectName = () => {
  const { name } = useAppSelector((state) => state.project.projectDraft);
  return <>{name ? name : EmptyValues.EnDash}</>;
};

const summaryAndDashboardRoutes: RouteObject[] = [
  {
    path: RouteConstants.PwermOpmConclusions,
    element: (
      <ProjectRoutesWrapper.PageLayout
        key={RouteConstants.PwermOpmConclusions}
        contentHeader={
          <ContentHeader
            title={l('_OverallConclusion')}
            controlOptions={
              <>
                <HighlightEditableCells /> <ShowPerShareFigures /> <CurrencyLabel />
              </>
            }
          />
        }
        pageName={RouteConstants.PwermOpmConclusions}>
        <PwermOpmConclusions />
      </ProjectRoutesWrapper.PageLayout>
    ),
    handle: {
      crumb: { title: () => l('_OverallConclusion') },
    },
  },
  {
    path: RouteConstants.DashboardOpm,
    element: (
      <ProjectRoutesWrapper.PageLayout
        key={RouteConstants.DashboardOpm}
        contentHeader={<DashboardHeader isChartScaled />}
        contentHeaderLayout={ContentHeaderLayout.Secondary}
        pageName={RouteConstants.DashboardOpm}>
        <DashboardOpm />
      </ProjectRoutesWrapper.PageLayout>
    ),
    handle: {
      crumb: { title: () => l('_OPMDashboard') },
    },
  },
  {
    path: RouteConstants.RealWorldDistributions,
    element: (
      <ProjectRoutesWrapper.PageLayout
        key={RouteConstants.RealWorldDistributions}
        contentHeader={<DashboardHeader isChartScaled />}
        contentHeaderLayout={ContentHeaderLayout.Secondary}
        pageName={RouteConstants.RealWorldDistributions}>
        <RealWorldDistributionsOpm />
      </ProjectRoutesWrapper.PageLayout>
    ),
    handle: {
      crumb: { title: () => l('_RealWorldDistributions') },
    },
  },
  {
    path: RouteConstants.SponsorIRRAndHurdle,
    element: <FocusModeOutlet />,
    children: [
      {
        index: true,
        element: <SponsorIRRAndHurdlePage />,
      },
      {
        path: RouteConstants.SponsorIRR,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.SponsorIRR}
            contentHeader={
              <ContentHeader
                title={l('_SponsorIRR')}
                navigation={<SponsorIRRAndHurdleNavigation />}
                subNavigation={
                  <CaseNavigationControls tabPage={SponsorIrrAndHurdleTab.SponsorIRR} />
                }
                focusMode={<SponsorIRRAndHurdleFocusMode />}
                controlOptions={
                  <>
                    <ExpandAllSponsorIRRAndHurdle tab={SponsorIRRAndHurdleTabs.SponsorIRR} />
                    <CurrencyLabel />
                  </>
                }
              />
            }
            pageName={RouteConstants.SponsorIRR}>
            <OutletByCaseId />
          </ProjectRoutesWrapper.PageLayout>
        ),
        children: [
          {
            path: ':caseId',
            element: <SponsorIRRTable />,
          },
        ],
      },
      {
        path: RouteConstants.IRRHurdleCalculations,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.IRRHurdleCalculations}
            contentHeader={
              <ContentHeader
                title={l('_IRRHurdleCalculations')}
                navigation={<SponsorIRRAndHurdleNavigation />}
                subNavigation={
                  <CaseNavigationControls tabPage={SponsorIrrAndHurdleTab.IRRHurdleCalculations} />
                }
                focusMode={<SponsorIRRAndHurdleFocusMode />}
                controlOptions={
                  <>
                    <ExpandAllSponsorIRRAndHurdle
                      tab={SponsorIRRAndHurdleTabs.IRRHurdleCalculations}
                    />
                    <CurrencyLabel />
                  </>
                }
              />
            }
            pageName={RouteConstants.IRRHurdleCalculations}>
            <OutletByCaseId />
          </ProjectRoutesWrapper.PageLayout>
        ),
        children: [
          {
            path: '',
            element: <IRRHurdleCalculations />,
          },
          {
            path: ':caseId',
            element: <IRRHurdleCalculations />,
          },
        ],
      },
    ],
    handle: {
      crumb: { title: () => l('_SponsorIRRAndHurdle') },
    },
  },
];

const inputsRoutes: RouteObject[] = [
  {
    path: RouteConstants.ProjectDetails,
    element: (
      <ProjectRoutesWrapper.PageLayout
        key={RouteConstants.ProjectDetails}
        contentHeader={
          <ContentHeader
            actions={
              <>
                <ProjectAccessAction standalone /> <CopyProjectAction /> <DeleteProjectAction />
              </>
            }
            title={l('_ProjectDetails')}
          />
        }
        pageName={RouteConstants.ProjectDetails}>
        <ProjectDetails />
      </ProjectRoutesWrapper.PageLayout>
    ),
    handle: {
      crumb: { title: () => l('_ProjectDetails') },
    },
  },
  {
    path: RouteConstants.CapitalStructure,
    element: <Outlet />,
    children: [
      {
        index: true,
        element: <CapitalStructurePage />,
      },
      {
        path: RouteConstants.BuildStructure,
        element: (
          <BuildStructureProvider>
            <ProjectRoutesWrapper.PageLayout
              key={RouteConstants.BuildStructure}
              contentHeader={
                <ContentHeader
                  title={l('_CapitalStructure')}
                  navigation={<CapitalStructureNavigation />}
                  subNavigation={<CapitalStructureTabContent isBuildStructure />}
                  actions={<AddEvent />}
                  controlOptions={<CurrencyLabel />}
                />
              }
              rightSidebar={<CapitalStructureSidebarErf />}
              pageName={RouteConstants.BuildStructure}>
              <Outlet />
            </ProjectRoutesWrapper.PageLayout>
          </BuildStructureProvider>
        ),
        children: [
          {
            path: '',
            element: <BuildStructureContentErf />,
          },
          {
            path: ':caseId',
            element: <BuildStructureContentErf />,
          },
        ],
      },
      {
        path: RouteConstants.PayoutLogic,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.PayoutLogic}
            contentHeader={
              <ContentHeader
                title={l('_CapitalStructure')}
                navigation={<CapitalStructureNavigation />}
                controlOptions={<CurrencyLabel />}
              />
            }
            pageName={RouteConstants.PayoutLogic}>
            <PayoutLogicMainErf>
              <Outlet />
            </PayoutLogicMainErf>
          </ProjectRoutesWrapper.PageLayout>
        ),
        children: [
          {
            path: ':instrumentId',
            element: <PayoutLogicErf />,
          },
        ],
      },
      {
        path: RouteConstants.CapTable,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.CapTable}
            contentHeader={
              <ContentHeader
                title={l('_CapitalStructure')}
                navigation={<CapitalStructureNavigation />}
                subNavigation={<CapitalStructureTabContent />}
                controlOptions={<CurrencyLabel />}
              />
            }
            pageName={RouteConstants.CapTable}>
            <Outlet />
          </ProjectRoutesWrapper.PageLayout>
        ),
        children: [
          {
            path: '',
            element: <CapTableErf />,
          },
          {
            path: ':caseId',
            element: <CapTableErf />,
          },
        ],
      },
      {
        path: RouteConstants.ParticipationTable,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.ParticipationTable}
            contentHeader={
              <ContentHeader
                title={l('_CapitalStructure')}
                navigation={<CapitalStructureNavigation />}
                subNavigation={<CapitalStructureTabContent />}
                controlOptions={
                  <>
                    <HighlightEditableCells /> <CurrencyLabel />
                  </>
                }
              />
            }
            pageName={RouteConstants.ParticipationTable}>
            <Outlet />
          </ProjectRoutesWrapper.PageLayout>
        ),
        children: [
          {
            path: '',
            element: <ParticipationTableErf />,
          },
          {
            path: ':caseId',
            element: <ParticipationTableErf />,
          },
        ],
      },
      {
        path: RouteConstants.SourcesAndUses,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.SourcesAndUses}
            contentHeader={
              <ContentHeader
                title={l('_CapitalStructure')}
                navigation={<CapitalStructureNavigation />}
                controlOptions={
                  <>
                    <HighlightEditableCells /> <CurrencyLabel />
                  </>
                }
              />
            }
            pageName={RouteConstants.SourcesAndUses}>
            <SourcesAndUsesContent />
          </ProjectRoutesWrapper.PageLayout>
        ),
      },
      {
        path: RouteConstants.EquityBridge,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.EquityBridge}
            contentHeader={
              <ContentHeader
                title={l('_CapitalStructure')}
                navigation={<CapitalStructureNavigation />}
                controlOptions={
                  <>
                    <HighlightEditableCells /> <CurrencyLabel />
                  </>
                }
              />
            }
            pageName={RouteConstants.EquityBridge}>
            <EquityBridgeContent />
          </ProjectRoutesWrapper.PageLayout>
        ),
      },
      {
        path: RouteConstants.Dividends,
        element: <DividendsPage />,
        children: [
          {
            path: '',
            element: <DividendsTable />,
          },
          {
            path: ':dividendSetId',
            element: <DividendsTable />,
          },
        ],
      },
    ],
    handle: {
      crumb: { title: () => l('_CapitalStructure') },
    },
  },
  {
    path: RouteConstants.DealThesis,
    element: <Outlet />,
    children: [
      {
        index: true,
        element: <DealThesisNavigation />,
      },
      {
        path: ':caseId',
        element: <DealThesisPage />,
      },
    ],
    handle: {
      crumb: {
        title: () => l('_DealThesis'),
      },
    },
  },
  {
    path: RouteConstants.ValuationInputs,
    element: <Outlet />,
    children: [
      {
        index: true,
        element: <ValuationInputsPage />,
      },
      {
        path: RouteConstants.CapmAssumptions,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.CapmAssumptions}
            contentHeader={
              <ContentHeader
                title={l('_ValuationInputs')}
                navigation={<ValuationInputsNavigation />}
                controlOptions={
                  <>
                    <HighlightEditableCells /> <EnableDiscountRateOverride />
                  </>
                }
              />
            }
            pageName={RouteConstants.CapmAssumptions}>
            <CapmAssumptions />
          </ProjectRoutesWrapper.PageLayout>
        ),
      },
      {
        path: RouteConstants.DiscountRateOverride,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.DiscountRateOverride}
            contentHeader={
              <ContentHeader
                title={l('_ValuationInputs')}
                navigation={<ValuationInputsNavigation />}
                controlOptions={
                  <>
                    <ExpandAllDiscountRateOverride /> <HighlightEditableCells />
                    <EnableDiscountRateOverride /> <EnableShowOriginalDiscountRates />
                  </>
                }
              />
            }
            pageName={RouteConstants.DiscountRateOverride}>
            <DiscountRateOverride />
          </ProjectRoutesWrapper.PageLayout>
        ),
      },
      {
        path: RouteConstants.DLOC,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.DLOC}
            contentHeader={
              <ContentHeader
                title={l('_ValuationInputs')}
                navigation={<ValuationInputsNavigation />}
                controlOptions={
                  <>
                    <HighlightEditableCells /> <EnableDiscountRateOverride />
                  </>
                }
              />
            }
            pageName={RouteConstants.DLOC}>
            <DLOC />
          </ProjectRoutesWrapper.PageLayout>
        ),
      },
      {
        path: RouteConstants.DLOM,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.DLOM}
            contentHeader={
              <ContentHeader
                title={l('_ValuationInputs')}
                navigation={<ValuationInputsNavigation />}
                controlOptions={
                  <>
                    <HighlightEditableCells /> <EnableDiscountRateOverride />
                  </>
                }
              />
            }
            pageName={RouteConstants.DLOM}>
            <DLOM />
          </ProjectRoutesWrapper.PageLayout>
        ),
      },
    ],
    handle: {
      crumb: { title: () => l('_ValuationInputs') },
    },
  },
  {
    path: RouteConstants.BenchmarkingInputs,
    element: (
      <ProjectRoutesWrapper.PageLayout
        key={RouteConstants.BenchmarkingInputs}
        contentHeader={
          <ContentHeader
            title={<ProjectName />}
            subtitle={l('_BenchmarkingInputs')}
            headerVariation={HeaderVariations.DEFAULT_SECONDARY}
            controlOptions={
              <>
                <BenchmarkingOnly /> <CurrencyLabel />
              </>
            }
          />
        }
        pageName={RouteConstants.BenchmarkingInputs}>
        <BenchmarkingInputs2 />
      </ProjectRoutesWrapper.PageLayout>
    ),
    handle: {
      crumb: { title: () => l('_BenchmarkingInputs') },
    },
  },
  {
    path: RouteConstants.OpmInputs,
    children: [
      {
        index: true,
        element: <OpmInputsPage />,
      },
    ],
    handle: {
      crumb: { title: () => l('_OpmInputs') },
    },
  },
];

const pwermWaterfallOutputsRoutes: RouteObject[] = [
  {
    path: RouteConstants.CostOfEquity,
    element: <Outlet />,
    children: [
      {
        index: true,
        element: <CostOfEquity2Page />,
      },
      {
        path: RouteConstants.Summary,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.Summary}
            contentHeader={
              <ContentHeader
                title={l('_CostOfEquityTitle')}
                navigation={<CostOfEquity2Navigation />}
                controlOptions={<CurrencyLabel />}
              />
            }
            pageName={RouteConstants.Summary}>
            <SummaryAndHighLevelData2 />,
          </ProjectRoutesWrapper.PageLayout>
        ),
      },
      {
        path: RouteConstants.TotalEquityCrossCheck,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.TotalEquityCrossCheck}
            contentHeader={
              <ContentHeader
                title={l('_CostOfEquityTitle')}
                navigation={<CostOfEquity2Navigation />}
                controlOptions={<CurrencyLabel />}
              />
            }
            pageName={RouteConstants.TotalEquityCrossCheck}>
            <TotalEquityCrossCheck2 />
          </ProjectRoutesWrapper.PageLayout>
        ),
      },
      {
        path: RouteConstants.InstrumentById,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.InstrumentById}
            contentHeader={
              <ContentHeader
                title={l('_CostOfEquityTitle')}
                navigation={<CostOfEquity2Navigation />}
                controlOptions={<CurrencyLabel />}
              />
            }
            pageName={RouteConstants.InstrumentById}>
            <InstrumentLevelData2 />
          </ProjectRoutesWrapper.PageLayout>
        ),
      },
    ],
    handle: {
      crumb: { title: () => l('_CostOfEquity') },
    },
  },
];

const pwerm2WaterfallOutputsRoutes: RouteObject[] = [
  {
    path: RouteConstants.PwermWaterfall,
    element: (
      <ProjectRoutesWrapper.PageLayout
        key={RouteConstants.PwermWaterfall}
        contentHeader={
          <ContentHeader
            title={l('_WaterfallPWERM')}
            controlOptions={<ExpandAllPwermWaterfallTable />}
          />
        }
        pageName={RouteConstants.PwermWaterfall}>
        <Pwerm2WaterfallTable />
      </ProjectRoutesWrapper.PageLayout>
    ),
    handle: {
      crumb: { title: () => l('_PWERMWaterfall') },
    },
  },
];

const pwerm2DashboardRoutes: RouteObject[] = [
  {
    path: RouteConstants.DashboardPwerm,
    element: (
      <ProjectRoutesWrapper.PageLayout
        key={RouteConstants.DashboardPwerm}
        contentHeader={<DashboardHeader isChartScaled />}
        contentHeaderLayout={ContentHeaderLayout.Secondary}
        pageName={RouteConstants.DashboardPwerm}>
        <Pwerm2Dashboard />
      </ProjectRoutesWrapper.PageLayout>
    ),
    handle: {
      crumb: { title: () => l('_PWERMDashboard') },
    },
  },
];

const opmWaterfallOutputsRoutes: RouteObject[] = [
  {
    path: RouteConstants.OpmWaterfall,
    element: <Outlet />,
    children: [
      {
        index: true,
        element: <OpmWaterfallPage />,
      },
      {
        path: RouteConstants.FirstLastSimulations,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.FirstLastSimulations}
            contentHeader={
              <ContentHeader
                title={l('_WaterfallOPM')}
                navigation={<OpmWaterfallNavigation />}
                controlOptions={
                  <>
                    <ExpandAllFirstLastWaterfall /> <CurrencyLabel />
                  </>
                }
              />
            }
            pageName={RouteConstants.FirstLastSimulations}>
            <FirstLastTableErf />,
          </ProjectRoutesWrapper.PageLayout>
        ),
      },
      {
        path: RouteConstants.SampleWaterfall,
        element: (
          <ProjectRoutesWrapper.PageLayout
            key={RouteConstants.SampleWaterfall}
            contentHeader={
              <ContentHeader
                title={l('_WaterfallOPM')}
                navigation={<OpmWaterfallNavigation />}
                actions={<AddSampleWaterfall />}
              />
            }
            pageName={RouteConstants.SampleWaterfall}>
            <SampleWaterfallTable />,
          </ProjectRoutesWrapper.PageLayout>
        ),
      },
    ],
    handle: {
      crumb: { title: () => l('_OPMWaterfall') },
    },
  },
];

const projectsRoutes: RouteObject[] = [
  {
    index: true,
    element: (
      <>
        <GlobalHistory />
        <Projects />
      </>
    ),
  },
  {
    path: ':projectId',
    element: (
      <ProjectRoutesWrapper>
        <Outlet />
      </ProjectRoutesWrapper>
    ),
    loader: async ({ params }: LoaderFunctionArgs) => {
      const projectId = params?.projectId;
      const project = store.getState().project;
      store.dispatch(clearPwerm2Results());
      store.dispatch(projectSlice.actions.updateIsPwermValid(false));
      if (project.status === LoadingStatus.Idle || project.status === LoadingStatus.Failed) {
        store.dispatch(setHighlightEditableCells(false));
        return store.dispatch(fetchAndRecalculate({ projectId: Number(projectId) }));
      }
      return null;
    },
    shouldRevalidate: ({ currentParams, nextParams }) => {
      return currentParams?.projectId !== nextParams?.projectId;
    },
    children: [
      { index: true, element: <PwcNavigate route={RouteConstants.DashboardPwerm} /> },
      ...summaryAndDashboardRoutes,
      ...pwerm2DashboardRoutes,
      ...inputsRoutes,
      ...pwermWaterfallOutputsRoutes,
      ...pwerm2WaterfallOutputsRoutes,
      ...opmWaterfallOutputsRoutes,
    ],
    handle: {
      crumb: {
        title: () => <ProjectName />,
      },
    },
  },
];

export default projectsRoutes;
